/**
 * Periodisk henting som hviler når appen ikke vises.
 *
 * Oversikten og detaljen spør Supabase hvert 10. sekund. Før dette gikk
 * intervallet også mens PWA-en lå i bakgrunnen, og det ble startet ETTER en
 * `await` i onMount — gikk man videre før første henting var ferdig, ble
 * intervallet aldri stoppet og hentet i det uendelige.
 *
 * Her startes intervallet med én gang (synkront), det pauses når fanen/appen
 * skjules, og det henter straks brukeren kommer tilbake så tallene er ferske.
 * Returnerer en stopp-funksjon — gi den rett tilbake fra onMount.
 */

type Synlighet = Pick<Document, 'hidden' | 'addEventListener' | 'removeEventListener'>;

export function startPolling(
  hent: () => unknown,
  intervallMs: number,
  doc: Synlighet | undefined = typeof document !== 'undefined' ? document : undefined,
): () => void {
  let timer: ReturnType<typeof setInterval> | undefined;
  let stoppet = false;

  const start = () => {
    if (timer === undefined) timer = setInterval(hent, intervallMs);
  };
  const pause = () => {
    if (timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
  };
  const vedSynlighet = () => {
    if (stoppet) return;
    if (doc?.hidden) {
      pause();
    } else if (timer === undefined) {
      hent(); // tilbake i appen → hent med én gang, ikke vent et helt intervall
      start();
    }
  };

  if (!doc?.hidden) start();
  doc?.addEventListener('visibilitychange', vedSynlighet);

  return () => {
    stoppet = true;
    pause();
    doc?.removeEventListener('visibilitychange', vedSynlighet);
  };
}
